/**
 * Native (per-currency) balance-sheet read — the ledger read behind the
 * balance-sheet document (`./assemble.ts`, screen and PDF) and the bookkeeper
 * AI's `get_balance_sheet` tool, so every surface reads the ledger through
 * exactly the same RPC and the same shaping.
 */
import { buildBalanceSheet } from "../../lib/balanceSheet.js";
import { previousFiscalYearEnd } from "../../lib/fiscalYear.js";
/**
 * Currency assumed for accounts with no `currency` set when the caller doesn't
 * supply the org's base currency.
 */
export const DEFAULT_BALANCE_SHEET_CURRENCY = "EUR";
/**
 * Fetch and assemble the native, per-currency balance sheet.
 *
 * Sign convention, the result split, current/non-current bucketing and
 * per-currency totals all live in `buildBalanceSheet`; this function is purely
 * the I/O around it: two reads of the same per-account aggregate, one at the
 * as-of date and one at the day before its fiscal year starts (the RPC's date
 * filter is inclusive, so the second read is exactly every prior fiscal year).
 */
export async function fetchBalanceSheetNative(client, { orgId, asOfDate, fiscalYearStartMonth, baseCurrency }) {
    const resolvedBaseCurrency = baseCurrency || DEFAULT_BALANCE_SHEET_CURRENCY;
    const priorYearsThrough = previousFiscalYearEnd(fiscalYearStartMonth, asOfDate);
    // Server-side aggregation: one row per account with opening balance and raw
    // debit/credit totals as of the date — avoids the 1,000-row PostgREST cap.
    const read = async (date) => {
        const { data, error } = await client.rpc("get_account_balances_as_of", {
            p_organization_id: orgId,
            p_as_of_date: date,
        });
        if (error)
            throw error;
        return data ?? [];
    };
    const [rows, priorYearsRows] = await Promise.all([read(asOfDate), read(priorYearsThrough)]);
    return {
        data: buildBalanceSheet(rows, priorYearsRows, resolvedBaseCurrency),
        baseCurrency: resolvedBaseCurrency,
    };
}
//# sourceMappingURL=fetch.js.map